import type { LucideIcon } from "lucide-react";
import { Users, UserPlus, FileText, XCircle, FileCheck, ClipboardList } from "lucide-react";

export type WidgetUIType = "STAT" | "LIST" | "DONUT" | "TREND";

export interface WidgetUIConfig {
  title: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  type: WidgetUIType;
  /** For LIST widgets: route for "View all" link. Omit for no link. */
  viewAllHref?: string;
}

/**
 * Global dashboard widget registry: widget key -> UI config (title, icon, color, bgColor, type).
 */
export const DASHBOARD_WIDGET_REGISTRY: Record<string, WidgetUIConfig> = {
  PATIENT_COUNT: {
    title: "Total Patients",
    icon: Users,
    color: "text-primary-600",
    bgColor: "bg-primary-100",
    type: "STAT",
  },
  DOCTOR_COUNT: {
    title: "Active Doctors",
    icon: UserPlus,
    color: "text-success-500",
    bgColor: "bg-success-50",
    type: "STAT",
  },
  MDT_CREATED_COUNT: {
    title: "MDT Created Count",
    icon: FileText,
    color: "text-info-600",
    bgColor: "bg-info-50",
    type: "STAT",
  },
  MDT_SUBMITTED_COUNT: {
    title: "MDT Submitted Count",
    icon: ClipboardList,
    color: "text-warning-600",
    bgColor: "bg-warning-50",
    type: "STAT",
  },
  MDT_APPROVED_COUNT: {
    title: "Approved MDTs",
    icon: FileText,
    color: "text-primary-600",
    bgColor: "bg-primary-100",
    type: "STAT",
  },
  MDT_REJECTED_COUNT: {
    title: "Rejected MDTs",
    icon: XCircle,
    color: "text-danger-600",
    bgColor: "bg-danger-100",
    type: "STAT",
  },
  MDT_TOTAL_COUNT: {
    title: "Total MDT Count",
    icon: FileCheck,
    color: "text-indigo-600",
    bgColor: "bg-indigo-100",
    type: "STAT",
  },
  AUDIT_LOG_LIST: {
    title: "Recent Activity",
    icon: ClipboardList,
    color: "text-neutral-600",
    bgColor: "bg-neutral-100",
    type: "LIST",
    viewAllHref: "/activity",
  },
  MDT_SUBMITTED_LIST: {
    title: "MDT Submitted",
    icon: ClipboardList,
    color: "text-primary-600",
    bgColor: "bg-primary-100",
    type: "LIST",
    viewAllHref: "/reports",
  },
  MDT_OVERVIEW_DONUT: {
    title: "MDT Overview",
    icon: FileText,
    color: "text-primary-600",
    bgColor: "bg-primary-100",
    type: "DONUT",
    viewAllHref: "/reports",
  },
  MDT_SUBMISSIONS_TREND: {
    title: "MDT Submissions Trend",
    icon: FileText,
    color: "text-primary-600",
    bgColor: "bg-primary-100",
    type: "TREND",
    viewAllHref: "/reports",
  },
  PATIENT_ADMISSIONS_TREND: {
    title: "Patient Admissions Trend",
    icon: Users,
    color: "text-primary-600",
    bgColor: "bg-primary-100",
    type: "TREND",
    viewAllHref: "/patients",
  },
  PATIENT_OVERVIEW_DONUT: {
    title: "Patients Overview",
    icon: Users,
    color: "text-primary-600",
    bgColor: "bg-primary-100",
    type: "DONUT",
    viewAllHref: "/patients",
  },
};

/** Get UI config for a widget key; prefer backend title when present; use registry for icons/colors. */
export function getWidgetUIConfig(
  key: string,
  backendTitle?: string
): { title: string; icon: LucideIcon; color: string; bgColor: string; type: WidgetUIType; viewAllHref?: string } {
  const config = DASHBOARD_WIDGET_REGISTRY[key];
  const Icon = config?.icon ?? FileText;
  return {
    title: backendTitle ?? config?.title ?? key,
    icon: Icon,
    color: config?.color ?? "text-neutral-600",
    bgColor: config?.bgColor ?? "bg-neutral-100",
    type: config?.type ?? "STAT",
    viewAllHref: config?.viewAllHref,
  };
}
